<script lang="ts">
  import { onMount, afterUpdate } from 'svelte';
  import Button from '../../Button.svelte';
  import Table from '../../Table.svelte';
  import Pagination from '../../Pagination.svelte';
  import Dropdown from '../../Dropdown.svelte';

  import PayrollDeductionsService, { 
    type PayrollDeduction, 
    type PayrollDeductionFilters 
  } from '../../../lib/api/admin/payroll-deductions.service';
  import StatusBadge from '../../StatusBadge.svelte';
  import { DonationRequestStatusEnum } from '../../../lib/api/admin/donation.service';
  import toast from 'svelte-french-toast';
  import { apiPageToUiPage } from '../../../lib/utils/pagination';

  // State
  let isLoading = true;
  let error = '';
  let payrollDeductions: PayrollDeduction[] = [];
  let totalPages = 1;
  let currentPage = 1;
  let totalItems = 0;
  let itemsPerPage = 15;

  // Filters
  let selectedEmployer = '';
  let startDate = '';
  let endDate = '';

  // Employer options for dropdown
  let employerOptions: Array<{ uuid: string; name: string }> = [];

  // Table columns configuration
  const tableColumns = [
    {
      key: 'name',
      label: 'Employer',
      width: '30%',
      render: (value: string, row: PayrollDeduction) => {
        return `<span class="employer-name">${value}</span>`;
      }
    },
    {
      key: 'status_name',
      label: 'Status',
      width: '30%',
      align: 'center',
      render: (value: string, row: PayrollDeduction) => {
        return `<div class="status-badge-wrapper" data-status="${row.donation_request_status.toLowerCase()}"></div>`;
      }
    },
    {
      key: 'total_deduction',
      label: 'Total Deduction',
      width: '25%',
      render: (value: number) => {
        const formattedValue = PayrollDeductionsService.formatCurrency(value);
        return `<span class="total-deduction">${formattedValue}</span>`;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '20%',
      render: (value: any, row: PayrollDeduction) => {
        return `<button class="view-details-btn" onclick="handleViewDetails('${row.uuid}')">View Details</button>`;
      }
    }
  ];

  // Load payroll deductions
  async function loadPayrollDeductions() {
    try {
      isLoading = true;
      error = '';
      
      const filters: PayrollDeductionFilters = {
        page: currentPage,
        per_page: itemsPerPage
      };
      
      if (selectedEmployer) filters.employer_uuid = selectedEmployer;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      
      const service = new PayrollDeductionsService();
      const response = await service.getPayrollDeductions(filters);
      
      if (response.success) {
        payrollDeductions = response.data || [];
        totalItems = response.paging?.total || 0;
        totalPages = response.paging?.last_page || 1;
        currentPage = apiPageToUiPage(response.paging?.current_page);
      } else {
        error = response.message || 'Failed to load payroll deductions';
        toast.error(error);
        payrollDeductions = [];
      }
    } catch (err) {
      error = 'Failed to load payroll deductions';
      toast.error(error);
      console.error('Error loading payroll deductions:', err);
      payrollDeductions = [];
    } finally {
      isLoading = false;
    }
  }

  // Load employer options for dropdown
  async function loadEmployerOptions() {
    try {
      // Extract unique employers from payroll deductions
      const uniqueEmployers = new Map<string, string>();
      payrollDeductions.forEach(deduction => {
        if (!uniqueEmployers.has(deduction.uuid)) {
          uniqueEmployers.set(deduction.uuid, deduction.name);
        }
      });
      
      employerOptions = Array.from(uniqueEmployers.entries()).map(([uuid, name]) => ({
        uuid,
        name
      }));
    } catch (err) {
      console.error('Error loading employer options:', err);
    }
  }

  // Event handlers

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    loadPayrollDeductions();
  }

  function handleEmployerChange(value: string) {
    selectedEmployer = value;
    currentPage = 1;
    loadPayrollDeductions();
  }

  function handleDateChange() {
    currentPage = 1;
    loadPayrollDeductions();
  }

  function handleProcessPayroll() {
    // TODO: Implement process payroll functionality
    toast.success('Processing payroll...');
  }

  function handleViewDetails(uuid: string) {
    // TODO: Navigate to payroll details page
    console.log('View details for:', uuid);
  }

  // Make handleViewDetails globally available for onclick handlers
  if (typeof window !== 'undefined') {
    (window as any).handleViewDetails = handleViewDetails;
  }

  // Add status badges after table update
  afterUpdate(() => {
    // Handle status badges
    const statusWrappers = document.querySelectorAll('.status-badge-wrapper');
    statusWrappers.forEach((wrapper) => {
      if (!wrapper.hasChildNodes()) {
        const status = wrapper.getAttribute('data-status');
        
        // Map API status to DonationRequestStatusEnum
        let mappedStatus: DonationRequestStatusEnum;
        switch (status?.toLowerCase()) {
          case 'pending':
            mappedStatus = DonationRequestStatusEnum.PENDING;
            break;
          case 'approved':
            mappedStatus = DonationRequestStatusEnum.APPROVED;
            break;
          case 'completed':
            mappedStatus = DonationRequestStatusEnum.PROCESSED;
            break;
          case 'rejected':
            mappedStatus = DonationRequestStatusEnum.REJECTED;
            break;
          default:
            mappedStatus = DonationRequestStatusEnum.PENDING;
        }
        
        // Create a new instance of StatusBadge component
        new StatusBadge({
          target: wrapper,
          props: {
            status: mappedStatus
          }
        });
      }
    });
  });

  // Initialize data
  onMount(async () => {
    await loadPayrollDeductions();
    await loadEmployerOptions();
  });
</script>

  <div class="payroll-deductions-page">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-left">
        <!-- Title moved to topbar -->
      </div>
      
      <div class="header-right">
        <div class="filter-item">
          <Dropdown
            options={employerOptions}
            selectedValue={selectedEmployer}
            placeholder="All Employers"
            onSelectionChange={handleEmployerChange}
          />
        </div>
        <div class="filter-item-btn">
          <Button variant="primary" on:click={handleProcessPayroll}>
            Process Payroll
          </Button>
        </div>
      </div>
    </div>

  <!-- Payroll Transactions Table -->
  <div class="table-section">
          {#if error}
        <div class="error-message">
          <p>{error}</p>
          <Button variant="secondary" on:click={loadPayrollDeductions}>Try Again</Button>
        </div>
      {:else}
        {#if isLoading}
          <div class="loading-message">Loading payroll deductions...</div>
        {:else if !payrollDeductions || payrollDeductions.length === 0}
          <div class="empty-message">No payroll deductions found</div>
        {:else}
          <Table 
            columns={tableColumns}
            data={payrollDeductions}
          />
        {/if}

      {#if totalPages > 1}
        <div class="pagination-wrapper">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            on:pageChange={handlePageChange}
          />
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .payroll-deductions-page {
    padding: 0;
    max-width: 100%;
    background-color: #f9fafb;
    min-height: 100vh;
  }

  /* Header Section */
  .page-header {
    padding: 0rem 1.5rem 0rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-left {
    display: flex;
    align-items: center;
  }

  .header-right {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .filter-item {
    min-width: 200px;
  }

  .filter-item {
    min-width: 200px;
  }

  /* Table Section */
  .table-section {
    background: white;
    margin: 1.5rem 2rem;
    border-radius: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .error-message {
    text-align: center;
    padding: 3rem;
    color: #ef4444;
  }

  .error-message p {
    margin-bottom: 1rem;
    font-size: 1rem;
  }

  .loading-message,
  .empty-message {
    text-align: center;
    padding: 3rem;
    color: #6b7280;
    font-size: 1rem;
  }

  .pagination-wrapper {
    margin-top: 1.5rem;
    padding: 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: center;
  }

  /* Table-specific styles */
  :global(.table-section table td) {
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0px;
  }

  :global(.employer-name) {
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0px;
    color: #111827;
  }

  :global(.total-deduction) {
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0px;
    color: #111827;
  }

  :global(.view-details-btn) {
    background: #f3f4f6;
    color: #374151;
    border: none;
    border-radius: 0.375rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  :global(.view-details-btn:hover) {
    background: #e5e7eb;
  }



  /* Responsive design */
  @media (max-width: 1024px) {
    .page-header {
      flex-direction: column;
      gap: 1rem;
      align-items: stretch;
    }

    .header-right {
      justify-content: space-between;
    }

  }

  @media (max-width: 768px) {
    .page-header {
      padding: 1rem;
    }

    .table-section {
      margin: 1rem;
    }

    .header-right {
      flex-direction: column;
      gap: 0.75rem;
    }

  }
</style> 